import React from "react";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { ShipperRow } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { COMPLETE_STATUS, DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useHeldIntermediateSurveys } from "../hooks/useHeldIntermediateSurveysStore";
import { useIsCargoOpsShippingAgent } from "../hooks/useIsCargoOpsShippingAgent";
import { closeOutActions } from "../utils/closeOutActions";
import PartyGrid, { type PartyGridRow, type PartyGridStatus } from "./PartyGrid";
import type { RowAction } from "./RowActionsMenu";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences.shippers-grid`;

const READINESS_KEYS = {
  PENDING_READINESS: "status-pending-readiness",
  READY: "status-ready",
} as const;

const VERDICT_KEYS = {
  CLOSE: { key: "verdict-close", badge: "success" },
  DISCREPANCY: { key: "verdict-discrepancy", badge: "error" },
} as const;

export type CloseOutKind = "intermediate" | "tolerance" | "history";

const ShippersGrid: React.FC<{
  shippers: ShipperRow[];
  operationId?: string;
  operationStatus?: string | null;
  onOpen: (code: string) => void;
  onCloseOut?: (kind: CloseOutKind, code: string) => void;
}> = ({ shippers, operationId = "", operationStatus = null, onOpen, onCloseOut }) => {
  const { t } = useTranslation();
  const isShippingAgent = useIsCargoOpsShippingAgent();
  const isOperations = useAuthStore((state) => state.isOperationsUser);
  const held = useHeldIntermediateSurveys(operationId);

  const statusCell = (row: ShipperRow): PartyGridStatus => {
    if (operationStatus === COMPLETE_STATUS) {
      const verdict = row.verdict ? VERDICT_KEYS[row.verdict] : null;
      return {
        prefix: t(`${L}.prefix-verdict`),
        label: verdict ? t(`${L}.${verdict.key}`) : "-",
        ...(verdict ? { badge: verdict.badge } : {}),
      };
    }
    return {
      prefix: t(`${L}.prefix-readiness`),
      label:
        row.readiness && row.readiness in READINESS_KEYS
          ? t(`${L}.${READINESS_KEYS[row.readiness]}`)
          : "-",
    };
  };

  const actionsOf = (row: ShipperRow): RowAction[] => {
    const code = row.shipperCompanyCode;
    if (!onCloseOut || !code) {
      return [];
    }
    const available = closeOutActions({
      status: operationStatus,
      row,
      isShippingAgent,
      isOperations,
      held: code in held.amounts,
    });
    return [
      ...(available.intermediate
        ? [
            {
              key: `${available.intermediate}-intermediate`,
              label: t(`${L}.actions.${available.intermediate}-intermediate`),
              onSelect: () => onCloseOut("intermediate", code),
            },
          ]
        : []),
      ...(available.editTolerance
        ? [
            {
              key: "edit-tolerance",
              label: t(`${L}.actions.edit-tolerance`),
              onSelect: () => onCloseOut("tolerance", code),
            },
          ]
        : []),
      ...(available.history
        ? [
            {
              key: "tolerance-history",
              label: t(`${L}.actions.tolerance-history`),
              onSelect: () => onCloseOut("history", code),
            },
          ]
        : []),
    ];
  };

  const rows: PartyGridRow[] = shippers.map((row) => ({
    code: row.shipperCompanyCode,
    planned: row.planned,
    actual: row.actual,
    intermediate: row.intermediaryAmount,
    heldIntermediate: row.shipperCompanyCode
      ? (held.amounts[row.shipperCompanyCode] ?? null)
      : null,
    adjusted: row.adjusted,
    tolerance: row.tolerancePct,
    splits: row.splits,
    status: statusCell(row).label,
    statusCell: statusCell(row),
    actions: actionsOf(row),
  }));

  return (
    <PartyGrid
      langKey="shippers-grid"
      testId="shippers-grid"
      rows={rows}
      withStatus
      withIntermediate
      withAdjusted
      withTolerance
      onOpen={onOpen}
    />
  );
};

export default ShippersGrid;
